import PropTypes from "prop-types";

import {
  ADMIN_CONSOLE_ADMIN_TOOLS_CONTENT_SETTINGS,
  VIEW_ARTICLE_VEILED_BOTTOM_UNAUTHENTICATED,
} from "./constants";
import { locationConfigError, componentConfigError } from "./errorMessages";
import NoAccessBanner from "./NoAccessBanner";
import propConfigMap from "./propConfigMap";
import useMeta from "./useMeta";
import Wrapper from "./Wrapper";

const getPropsFor = (location, component) => {
  const locationConfig = propConfigMap.get(location);
  if (!locationConfig) return locationConfigError();

  const componentProps = locationConfig?.[component];
  if (!componentProps) return componentConfigError();

  return componentProps;
};

const propTypes = {
  HideWhenUnlockOrPreviewContentBannerIsShown: {
    pageName: PropTypes.string.required,
    children: PropTypes.node.required,
  },
  UnlockOrPreviewContentBanner: {
    location: PropTypes.string.required,
  },
};

export const HideWhenUnlockOrPreviewContentBannerIsShown = ({
  pageName,
  children,
}) => {
  const { isLiteUser, isStudent } = useMeta();

  const adminContentSettingsPage =
    pageName === ADMIN_CONSOLE_ADMIN_TOOLS_CONTENT_SETTINGS;

  // Under no circumstances should a lite unlock banner ever
  // be shown to a student as this is a banner asking for a user
  // to upgrade their account for access to features.
  if (isStudent) return children;

  // Hide for lite users when on particular admin pages.
  // Note: Originally this was not neccessary as
  // all admin pages that used this were configured
  // to display banners, but because the requirements
  // were adjusted this will ensure we hide the elements
  // in the correct situations.
  const excludeContentPage = adminContentSettingsPage;

  if (isLiteUser && excludeContentPage) return null;

  // Default to showing children content unless a late resolving
  // hook determines otherwise by checking the qualifiers.
  return children;
};

HideWhenUnlockOrPreviewContentBannerIsShown.propTypes =
  propTypes.HideWhenUnlockOrPreviewContentBannerIsShown;

export const UnlockOrPreviewContentBanner = ({ location }) => {
  const { isLiteUser, isStudent, isParent } = useMeta();

  // Unauthenticated view bottom banner is the only banner
  // that can be accessed without any of the checks
  if (location !== VIEW_ARTICLE_VEILED_BOTTOM_UNAUTHENTICATED) {
    // Under no circumstances should a lite unlock banner ever
    // be shown to a student as this is a banner asking for a user
    // to upgrade their account for access to features.
    if (isStudent) return null;

    // Don't show the banner to people that don't meet the minimum
    // requirements of being an isLiteUser.
    if (!isLiteUser) return null;
  }

  // Account for Caregiver states which were
  // added to scope and designs around ~04.05.23
  // Since we early disqualified students and non isLiteUsers
  // We can now ensure we provide the alternative banner for
  // parents / caregivers since we know they are
  if (isParent) {
    // Add 'Caregiver' to location for a
    // lookup check in the configuration map.
    const caregiverPermutationKey = location + "Caregiver";

    // If no caregiver permutation is registered in the config
    // we will not show a banner for a caregiver.
    if (propConfigMap.has(caregiverPermutationKey)) {
      // Since there is a config registered adjust location so
      // that we will use that permutation for displaying.
      location = caregiverPermutationKey;
    }
  }

  const wrapperProps = getPropsFor(location, "Wrapper");

  const noAccessBannerProps = getPropsFor(location, "NoAccessBanner");

  return (
    <Wrapper {...wrapperProps}>
      <NoAccessBanner {...noAccessBannerProps} />
    </Wrapper>
  );
};

UnlockOrPreviewContentBanner.propTypes = propTypes.UnlockOrPreviewContentBanner;

export default UnlockOrPreviewContentBanner;
